import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { TapeBadge } from './TapeBadge';
import { Sticker } from './Sticker';
import { Marquee } from './Marquee';
export function Hero() {
  return <section className="relative min-h-screen overflow-hidden bg-otter-black pt-20 md:pt-24" data-id="element-185">
      {/* Arizona backyard backdrop */}
      <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
      backgroundImage: "url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=80')"
    }} data-id="element-186" />
      <div className="absolute inset-0 bg-gradient-to-b from-otter-black via-otter-black/85 to-otter-black" data-id="element-187" />

      {/* Background glow */}
      <div className="absolute -right-1/4 top-0 h-[800px] w-[800px] rounded-full bg-otter-cyan/20 blur-[120px]" data-id="element-188" />

      {/* Sunburst — way back */}
      <div className="pointer-events-none absolute -right-[30%] top-1/2 z-0 h-[120vh] w-[120vh] -translate-y-1/2 md:-right-[10%] md:h-[140vh] md:w-[140vh]" data-id="element-189">
        <div className="absolute inset-0 animate-spin-slow opacity-25" style={{
        animationDuration: '60s'
      }} data-id="element-190">
          {[...Array(16)].map((_, i) => <div key={i} className="absolute left-1/2 top-1/2 h-full w-16 origin-top -translate-x-1/2 bg-otter-cyan/30" style={{
          transform: `rotate(${i * 22.5}deg) translateY(-50%)`
        }} data-id="element-191" />)}
        </div>
        <div className="absolute left-1/2 top-1/2 h-[40%] w-[40%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-otter-cyan/15 blur-3xl" data-id="element-192" />
      </div>

      <div className="absolute inset-0 bg-halftone bg-halftone-size opacity-30" data-id="element-193" />

      {/* ============== DESKTOP: absolute-positioned Case anchored top-right ============== */}
      <div className="pointer-events-none absolute inset-0 z-[5] hidden md:block" data-id="element-194">
        <motion.div initial={{
        opacity: 0,
        scale: 0.92,
        y: -20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="absolute right-0 bottom-[60px] top-[4%] w-[62%] md:top-[5%] md:w-[60%] lg:top-[3%] lg:w-[62%] lg:bottom-[64px] xl:top-[2%] xl:w-[60%]" data-id="element-195">
          <img src="https://cdn.magicpatterns.com/uploads/p9voaUqC263KrfJEVpiKD1/39.png" alt="Case ready to clean" className="h-full w-full object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" style={{
          objectPosition: '60% bottom'
        }} data-id="element-196" />

          {/* Floating stickers around Case */}
          <div className="pointer-events-auto" data-id="element-197">
            <Sticker rotation={-12} className="bottom-[20%] left-0 sm:left-2 lg:left-8" data-id="element-198">
              <div className="text-center leading-none" data-id="element-199">
                <span className="block text-3xl" data-id="element-200">5★</span>
                <span className="block text-sm" data-id="element-201">RATED</span>
              </div>
            </Sticker>

            <Sticker rotation={8} className="right-4 top-[10%] bg-otter-bone lg:right-12" data-id="element-202">
              <div className="text-center leading-none" data-id="element-203">
                <span className="block text-xl" data-id="element-204">GREASE =</span>
                <span className="block text-2xl text-otter-blaze" data-id="element-205">GONE</span>
              </div>
            </Sticker>
          </div>
        </motion.div>
      </div>

      {/* ============== CONTENT: text + mobile image ============== */}
      <div className="container relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] flex-col px-4 pb-24 md:min-h-[calc(100vh-6rem)] md:flex-row md:items-center md:px-8 md:pb-32" data-id="element-206">
        {/* Left / Text */}
        <div className="flex w-full flex-col items-start justify-center md:w-[55%] lg:w-1/2" data-id="element-207">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="w-full" data-id="element-208">
            <TapeBadge text="WE DON'T JUST CLEAN." color="blaze" rotation={-3} className="mb-5 text-base sm:mb-6 sm:text-xl" data-id="element-209" />

            <h1 className="font-display text-[3.25rem] leading-[0.85] tracking-tighter text-otter-bone xs:text-[3.75rem] sm:text-[5rem] md:text-[5.5rem] lg:text-[8rem] xl:text-[10rem]" data-id="element-210">
              YOUR GRILL <br data-id="element-211" />
              IS <span className="text-otter-cyan" data-id="element-212">GROSS.</span>
            </h1>

            <div className="relative mt-2 inline-block" data-id="element-213">
              <h2 className="font-display text-[2.5rem] leading-none tracking-tight text-otter-bone sm:text-[3.5rem] md:text-[3.75rem] lg:text-[6rem]" data-id="element-214">
                WE FIX THAT.
              </h2>
              <svg className="absolute -bottom-3 left-0 w-full text-otter-cyan sm:-bottom-4" viewBox="0 0 300 20" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-215">
                <path d="M2 15C50 5 150 -5 298 12" stroke="currentColor" strokeWidth="6" strokeLinecap="round" data-id="element-216" />
              </svg>
            </div>

            <p className="mt-7 max-w-xl font-body text-base text-otter-bone/80 sm:mt-8 sm:text-lg lg:text-xl" data-id="element-217">
              Pro deep-cleaning that makes your BBQ look brand new.
              Steam-scrubbed. Sanitized. Sealed. Stop cooking on last year's
              grime.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4 lg:mt-10" data-id="element-218">
              <a href="#contact" className="group flex items-center justify-center gap-2 border-4 border-otter-cyan bg-otter-cyan px-6 py-3 font-display text-xl tracking-wider text-otter-black transition-all hover:-translate-y-1 hover:shadow-brutal-cyan sm:px-8 sm:py-4 sm:text-2xl" data-id="element-219">
                BOOK A CLEAN{' '}
                <ArrowRight className="transition-transform group-hover:translate-x-1" data-id="element-220" />
              </a>
              <a href="#gallery" className="flex items-center justify-center gap-2 border-4 border-otter-bone bg-transparent px-6 py-3 font-display text-xl tracking-wider text-otter-bone transition-all hover:-translate-y-1 hover:bg-otter-bone hover:text-otter-black sm:px-8 sm:py-4 sm:text-2xl" data-id="element-221">
                SEE THE GLOW-UP
              </a>
            </div>
          </motion.div>
        </div>

        {/* ============ MOBILE: Case as graphic insert ============ */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="relative mx-auto mt-10 block w-full max-w-[420px] md:hidden" data-id="element-222">
          {/* Cyan halftone disc backdrop */}
          <div className="absolute inset-x-6 top-4 aspect-square rounded-full bg-otter-cyan/90" data-id="element-223" />
          <div className="absolute inset-x-6 top-4 aspect-square rounded-full opacity-40 mix-blend-multiply" style={{
          backgroundImage: 'radial-gradient(circle, #0A0A0A 1.5px, transparent 2px)',
          backgroundSize: '8px 8px'
        }} data-id="element-224" />

          {/* Spinning rays mini */}
          <div className="absolute inset-x-6 top-4 aspect-square animate-spin-slow opacity-50" style={{
          animationDuration: '40s'
        }} data-id="element-225">
            {[...Array(12)].map((_, i) => <div key={i} className="absolute left-1/2 top-1/2 h-1/2 w-2 origin-top -translate-x-1/2 bg-otter-black/20" style={{
            transform: `rotate(${i * 30}deg) translateY(-50%)`
          }} data-id="element-226" />)}
          </div>

          {/* Case image — clipped within disc area */}
          <img src="https://cdn.magicpatterns.com/uploads/p9voaUqC263KrfJEVpiKD1/39.png" alt="Case ready to clean" className="relative z-10 mx-auto block h-[420px] w-auto object-contain object-bottom drop-shadow-2xl" data-id="element-227" />

          {/* Mobile stickers */}
          <div className="pointer-events-none absolute -bottom-2 -left-2 z-20 rotate-[-12deg] rounded-full border-4 border-otter-black bg-otter-cyan px-4 py-3 font-display shadow-brutal" data-id="element-228">
            <span className="block text-center text-2xl leading-none text-otter-black" data-id="element-229">
              5★
            </span>
            <span className="block text-center text-[10px] tracking-widest text-otter-black" data-id="element-230">
              RATED
            </span>
          </div>
          <div className="pointer-events-none absolute -right-1 top-6 z-20 rotate-[8deg] rounded-full border-4 border-otter-black bg-otter-bone px-3 py-2 font-display shadow-brutal" data-id="element-231">
            <span className="block text-center text-xs leading-none text-otter-black" data-id="element-232">
              GREASE =
            </span>
            <span className="block text-center text-base leading-tight text-otter-blaze" data-id="element-233">
              GONE
            </span>
          </div>
        </motion.div>
      </div>

      {/* Bottom Marquee */}
      <div className="absolute bottom-0 w-full border-y-4 border-otter-black bg-otter-cyan py-2 sm:py-3" data-id="element-234">
        <Marquee text="GRIME OUT • SHINE IN • GRIME OUT • SHINE IN • GRIME OUT • SHINE IN •" className="font-display text-xl tracking-widest text-otter-black sm:text-2xl lg:text-3xl" data-id="element-235" />
      </div>
    </section>;
}